/**
 * CalendarSettingsDialog — per-user preferences UI (Vague 113).
 *
 * Render grouped by the same 4 categories as the backend model. Every field
 * updates the parent's `settings` state + persists instantly (optimistic).
 */
import React from 'react';
import { Settings2, RotateCcw, Clock, Calendar, Bell, Cog, X } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';

const DOWS = ['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche'];

export default function CalendarSettingsDialog({ open, onClose, settings, onChange, onReset }) {
  const patch = (k) => (v) => onChange({ [k]: typeof v === 'object' && v?.target ? v.target.value : v });
  const num = (k) => (e) => onChange({ [k]: Number(e.target.value) });
  const bool = (k) => (v) => onChange({ [k]: !!v });
  const reminderOptions = [0, 5, 15, 30, 60, 120, 1440];
  const toggleReminder = (m) => {
    const s = new Set(settings.default_reminders || []);
    if (s.has(m)) s.delete(m); else s.add(m);
    onChange({ default_reminders: Array.from(s).sort((a,b) => a-b) });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="cal-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings2 className="w-5 h-5 text-[#4F46E5]" /> Paramètres du calendrier</DialogTitle>
          <DialogDescription>Personnalisez votre expérience. Les préférences se synchronisent entre vos appareils.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 max-h-[65vh] overflow-y-auto pr-2">
          {/* Horaires & grille */}
          <Section icon={Clock} title="Horaires & grille">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Début de journée">
                <select value={settings.start_hour} onChange={num('start_hour')} className={SEL} data-testid="cal-set-start-hour">
                  {Array.from({length:24},(_,i)=>i).map((h)=> <option key={h} value={h}>{String(h).padStart(2,'0')}:00</option>)}
                </select>
              </Field>
              <Field label="Fin de journée">
                <select value={settings.end_hour} onChange={num('end_hour')} className={SEL} data-testid="cal-set-end-hour">
                  {Array.from({length:24},(_,i)=>i+1).map((h)=> <option key={h} value={h}>{String(h).padStart(2,'0')}:00</option>)}
                </select>
              </Field>
              <Field label="Granularité des créneaux">
                <select value={settings.slot_minutes} onChange={num('slot_minutes')} className={SEL} data-testid="cal-set-slot">
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                </select>
              </Field>
              <Field label="Durée d'événement par défaut">
                <select value={settings.default_duration_minutes} onChange={num('default_duration_minutes')} className={SEL} data-testid="cal-set-duration">
                  {[15,30,45,60,90,120].map((m)=> <option key={m} value={m}>{m} min</option>)}
                </select>
              </Field>
              <Field label="Premier jour de la semaine">
                <select value={settings.first_day_of_week} onChange={num('first_day_of_week')} className={SEL} data-testid="cal-set-dow">
                  {DOWS.map((lbl,i)=> <option key={i} value={i}>{lbl}</option>)}
                </select>
              </Field>
            </div>
          </Section>

          {/* Vue & affichage */}
          <Section icon={Calendar} title="Vue & affichage">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Vue par défaut">
                <select value={settings.default_view} onChange={patch('default_view')} className={SEL} data-testid="cal-set-view">
                  <option value="month">Mois</option>
                  <option value="workweek">Semaine 5 jours</option>
                  <option value="week">Semaine 7 jours</option>
                  <option value="day">Jour</option>
                  <option value="agenda">Liste</option>
                </select>
              </Field>
            </div>
            <div className="space-y-2">
              <Toggle checked={settings.show_week_numbers} onChange={bool('show_week_numbers')} label="Afficher les numéros de semaine" testid="cal-set-weeknums" />
              <Toggle checked={settings.hide_done} onChange={bool('hide_done')} label="Masquer les événements terminés" testid="cal-set-hide-done" />
              <Toggle checked={settings.hide_cancelled} onChange={bool('hide_cancelled')} label="Masquer les événements annulés" testid="cal-set-hide-cancelled" />
            </div>
          </Section>

          {/* Rappels */}
          <Section icon={Bell} title="Rappels">
            <div>
              <div className="text-[11.5px] text-[#6B6C85] mb-2">Rappels ajoutés par défaut aux nouveaux événements :</div>
              <div className="flex flex-wrap gap-1.5">
                {reminderOptions.map((m) => {
                  const active = (settings.default_reminders || []).includes(m);
                  const label = m === 0 ? 'Au moment' : m < 60 ? `${m} min` : m === 60 ? '1 h' : m < 1440 ? `${m/60} h` : '1 jour';
                  return (
                    <button key={m} type="button" onClick={() => toggleReminder(m)} data-testid={`cal-set-default-rem-${m}`}
                            className={`h-8 px-2.5 rounded-lg text-[11.5px] font-semibold border ${active ? 'bg-[#4F46E5] text-white border-[#4F46E5]' : 'bg-white border-[#EFF0F6] text-[#6B6C85] hover:border-[#C7D2FE]'}`}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2 mt-3">
              <Toggle checked={settings.toast_reminder} onChange={bool('toast_reminder')} label="Toast dans l'application" testid="cal-set-toast" />
              <Toggle checked={settings.email_reminder} onChange={bool('email_reminder')} label="Email de rappel" testid="cal-set-email" />
            </div>
          </Section>

          {/* Avancés */}
          <Section icon={Cog} title="Avancés">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Fuseau horaire affiché">
                <select value={settings.timezone} onChange={patch('timezone')} className={SEL} data-testid="cal-set-tz">
                  <option value="Europe/Paris">Europe/Paris</option>
                  <option value="Europe/London">Europe/London</option>
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">America/New_York</option>
                  <option value="America/Los_Angeles">America/Los_Angeles</option>
                </select>
              </Field>
              <Field label="Couleur des événements sans type">
                <input type="color" value={settings.default_color} onChange={patch('default_color')} className="h-10 w-20 rounded-lg border border-[#EFF0F6] cursor-pointer" data-testid="cal-set-color" />
              </Field>
            </div>
            <Toggle checked={settings.show_team_members} onChange={bool('show_team_members')} label="Afficher les événements de mes collègues (lecture seule)" testid="cal-set-team" />
          </Section>
        </div>

        <DialogFooter className="flex items-center justify-between">
          <button onClick={onReset} data-testid="cal-set-reset"
                  className="h-10 px-3 rounded-xl border border-[#EFF0F6] text-[#6B6C85] text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#FAFAFE]">
            <RotateCcw className="w-4 h-4" /> Réinitialiser
          </button>
          <button onClick={onClose} data-testid="cal-set-close"
                  className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2">
            <X className="w-4 h-4" /> Fermer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SEL = "w-full h-10 rounded-lg border border-[#EFF0F6] px-2 text-[12.5px] bg-white";

function Section({ icon: Icon, title, children }) {
  return (
    <div className="rounded-xl border border-[#EFF0F6] p-4 bg-[#FAFAFE]">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[#4F46E5]" />
        <h3 className="text-[13px] font-bold text-[#1A1B2E]">{title}</h3>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-[#8E8FA5] block mb-1">{label}</label>
      {children}
    </div>
  );
}
function Toggle({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-center justify-between gap-3 cursor-pointer py-1" data-testid={testid}>
      <span className="text-[12.5px] text-[#4A4B63]">{label}</span>
      <button type="button" onClick={() => onChange(!checked)}
              className={`relative w-10 h-6 rounded-full transition-colors ${checked ? 'bg-[#4F46E5]' : 'bg-[#D1D5DB]'}`}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-4' : ''}`} />
      </button>
    </label>
  );
}
